import React from 'react';
import { Dices, Globe } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, useI18n } from '../i18n';

const LANGUAGE_LABELS = { fi: 'Suomi', en: 'English', sv: 'Svenska' };
const LEGAL_PATHS = [
  ['license', '/legal/license', 'licenseLink'],
  ['privacy', '/legal/privacy', 'privacyLink'],
  ['terms', '/legal/terms', 'termsLink']
];

const LegalPage = ({ type, standalone = false }) => {
  const { t, language, setLanguage } = useI18n();
  const legal = t.legal;
  const document = legal[type] || legal.license;

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f5', color: '#1f2937' }}>
      {standalone && (
        <header style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          padding: '1rem max(1rem, calc((100vw - 960px) / 2))',
          background: '#111827',
          color: '#fff'
        }}>
          <Link to="/" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#fff', textDecoration: 'none', fontWeight: 700 }}>
            <Dices size={22} /> {legal.brand}
          </Link>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <Globe size={16} aria-hidden="true" />
            <span className="sr-only">{legal.languageLabel}</span>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLanguage(lang)}
                aria-pressed={language === lang}
                style={{
                  border: language === lang ? '1px solid #f59e0b' : '1px solid #4b5563',
                  background: language === lang ? '#f59e0b' : 'transparent',
                  color: language === lang ? '#111827' : '#fff',
                  borderRadius: '6px',
                  padding: '0.3rem 0.55rem',
                  cursor: 'pointer'
                }}
              >
                {LANGUAGE_LABELS[lang]}
              </button>
            ))}
          </div>
        </header>
      )}

      <nav aria-label={legal.menu} style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '1rem',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb'
      }}>
        {LEGAL_PATHS.map(([id, path, label]) => (
          <NavLink
            key={id}
            to={path}
            style={({ isActive }) => ({
              color: isActive ? '#92400e' : '#374151',
              background: isActive ? '#fef3c7' : 'transparent',
              borderRadius: '6px',
              padding: '0.45rem 0.7rem',
              fontWeight: isActive ? 700 : 500,
              textDecoration: 'none'
            })}
          >
            {legal[label]}
          </NavLink>
        ))}
      </nav>

      <main style={{ maxWidth: '820px', margin: '0 auto', padding: '2rem 1.25rem 4rem', lineHeight: 1.65 }}>
        <h1 style={{ color: '#111827', marginBottom: '0.25rem' }}>{document.title}</h1>
        <p style={{ color: '#6b7280', marginTop: 0 }}>{document.effective}</p>
        <p style={{ fontWeight: 600 }}>{legal.provider}</p>
        <p style={{ color: '#4b5563' }}>{legal.contact}</p>
        {(document.sections || []).map((section) => (
          <section key={section.title} style={{ marginTop: '2rem' }}>
            <h2 style={{ color: '#111827', fontSize: '1.25rem' }}>{section.title}</h2>
            {(section.paragraphs || []).map((paragraph, index) => (
              <p key={`${section.title}-p-${index}`}>{paragraph}</p>
            ))}
            {section.bullets?.length > 0 && (
              <ul>
                {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
              </ul>
            )}
          </section>
        ))}

        {standalone && (
          <p style={{ marginTop: '3rem' }}>
            <Link to="/" style={{ color: '#92400e', fontWeight: 600 }}>{legal.backHome}</Link>
          </p>
        )}
      </main>
    </div>
  );
};

export default LegalPage;
